<template>
  <div class="campaign-card" @click="onClick">
    <!-- Tags -->
    <div class="card-tags">
      <span class="tag-discount"><el-icon><MagicStick /></el-icon> ลดพิเศษ</span>
      <span class="tag-date" v-if="campaign.endDate">ถึง {{ formatDate(campaign.endDate) }}</span>
    </div>
    
    <div class="card-body">
      <!-- Left: Image -->
      <div 
        class="card-img"
        :style="campaign.imageUrl ? { backgroundImage: `url(${getApiUrl(campaign.imageUrl)})` } : {}"
      >
        <!-- Fallback icon if no image -->
        <el-icon v-if="!campaign.imageUrl" :size="32" color="#94a3b8"><Picture /></el-icon>
      </div>

      <!-- Right: Content -->
      <div class="card-info">
        <h3>{{ campaign.title }}</h3>
        <p class="card-desc">{{ campaign.description }}</p>

        <!-- Info pills (Dynamic from features) -->
        <div class="info-pills" v-if="displayFeatures && displayFeatures.filter(Boolean).length > 0">
          <div class="pill" v-for="(feat, idx) in displayFeatures.filter(Boolean)" :key="idx">
            <el-icon v-if="idx === 0"><DocumentChecked /></el-icon>
            <el-icon v-else-if="idx === 1"><User /></el-icon>
            <el-icon v-else><Timer /></el-icon>
            {{ feat }}
          </div>
        </div>

        <div class="card-bottom">
          <div class="price-section">
            <div class="old-price" v-if="packageInfo?.price != null">
              ฿ {{ packageInfo.price.toLocaleString() }}
            </div>
            <div class="current-price-row">
              <span class="price-label">พิเศษเพียง</span>
              <span class="price-val">
                <span class="currency">฿</span> {{ (campaign.priceSale ?? 0).toLocaleString() }}
              </span>
            </div>
            <div class="end-date" v-if="campaign.endDate">
              <el-icon><Calendar /></el-icon> ลดถึง {{ formatDate(campaign.endDate) }}
            </div>
          </div>
          <button class="btn-detail" @click.stop="onClick">
            ดูรายละเอียด <el-icon><ArrowRight /></el-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- Expandable detail slot (if they want to see "รวมในแพ็กเกจ") -->
    <div class="card-footer" v-if="displayExamItems && displayExamItems.length > 0">
      <div class="footer-title">
        <el-icon color="#3b82f6"><Present /></el-icon> รวมในแพ็กเกจ
      </div>
      <ul class="feature-list">
        <li v-for="(item, idx) in displayExamItems.slice(0, 4)" :key="idx">
          <el-icon color="#3b82f6"><Select /></el-icon> {{ item }}
        </li>
      </ul>
      <div class="footer-more" v-if="displayExamItems.length > 4">
        <button @click.stop="onClick">ดูรายการทั้งหมด <el-icon><ArrowDown /></el-icon></button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { 
  MagicStick, Picture, DocumentChecked, User, Timer, 
  ArrowRight, Present, Select, ArrowDown 
} from '@element-plus/icons-vue';
import type { CampaignView } from "@/api/promotion.api";

const props = defineProps<{
  campaign: CampaignView;
  packageInfo: any;
}>();

const emit = defineEmits<{
  (e: 'click', campaign: CampaignView): void;
}>();

import { computed } from 'vue';

const displayExamItems = computed(() => {
  if (props.packageInfo && props.packageInfo.examItems && props.packageInfo.examItems.length > 0) {
    return props.packageInfo.examItems;
  }
  return props.campaign?.examItems || [];
});

const displayFeatures = computed(() => {
  if (props.packageInfo && props.packageInfo.features && props.packageInfo.features.length > 0) {
    return props.packageInfo.features;
  }
  return props.campaign?.features || [];
});

function onClick() {
  emit('click', props.campaign);
}

function getApiUrl(path: string): string {
  if (!path) return '';
  if (path.startsWith("http")) return path;
  const baseUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || "/api/v1";
  const base = baseUrl.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
}
</script>

<style scoped>
.campaign-card {
  background: white;
  border-radius: 20px;
  padding: 16px;
  margin: 0 16px 16px; /* Added margin for left, right, and bottom spacing */
  box-shadow: 0 4px 20px rgba(0,0,0,0.03);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}
.campaign-card:active {
  transform: scale(0.98);
}
.card-tags {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.tag-discount {
  background: #f43f5e;
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 4px;
}
.tag-date {
  color: #f43f5e;
  font-size: 12px;
  font-weight: 600;
}
.card-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.card-img {
  width: 100%;
  height: 160px;
  background-color: #f1f5f9;
  border-radius: 12px;
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
}
.card-info {
  display: flex;
  flex-direction: column;
}
.card-info h3 {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.4;
}
.card-desc {
  margin: 0 0 12px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.info-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
}
.pill {
  background: #f1f5f9;
  color: #475569;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.card-bottom {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
}
.price-section {
  display: flex;
  flex-direction: column;
}
.old-price {
  font-size: 12px;
  color: #94a3b8;
  text-decoration: line-through;
  margin-bottom: 2px;
}
.current-price-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.price-label {
  background: #f43f5e;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
}
.price-val {
  font-size: 22px;
  font-weight: 800;
  color: #f43f5e;
}
.price-val .currency {
  font-size: 14px;
}
.end-date {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.btn-detail {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  cursor: pointer;
}

.card-footer {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px dashed #e2e8f0;
}
.footer-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
}
.feature-list {
  list-style: none;
  padding: 0;
  margin: 0 0 12px;
}
.feature-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
}
.footer-more {
  display: flex;
  justify-content: flex-end;
}
.footer-more button {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  color: #3b82f6;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}
</style>
